import { createSelector } from '@reduxjs/toolkit';
import { selectSheet, selectSheetView } from './sheetSelector';
import { Sheet, SheetView } from '../types';
import { RootState } from '../../core/redux/store';
import { getRowHeaderWidth } from '../utils/sheet/uiData';
import { SheetDefaults } from '../constants/sheetDefaults';
import { defaultFreeze } from '../utils/sheet/metaData';
import { getSectionHeight, getSectionWidth } from '../../sections/utils';
import { getDefaultArea, getDefaultPosition } from '../utils/area/area';
import { getDefaultVirtualViewport } from '../utils/viewport/viewportUtils';
import { defaultFilterResults } from '../utils/views/views';
import _ from 'lodash';
import { getViewportStartEnd } from '../utils/views/virtualViewport';

export interface ReturnTypeofSheetSectionsSelector
  extends Pick<
      Sheet,
      | 'defaultColWidth'
      | 'defaultRowHeight'
      | 'colSizes'
      | 'rowSizes'
      | 'freezedCols'
      | 'freezedRows'
      | 'zoom'
      | 'cells'
      | 'cellsReferences'

      // | 'filterResult'
      | 'merges'
      | 'colGroups'
      | 'rowGroups'
      | 'hiddenRows'
      | 'hiddenCols'
      | 'maxColIndex'
      | 'maxRowIndex'
      // | 'conditionalFormats'
      // | 'cellNumberFormats'
      | 'numberFormatMatrix'
      | 'numberFormats'
      | 'stylesMatrix'
      | 'styles'
      // | 'dataValidations'
      | 'dataValidationMatrix'
      | 'dataValidationsMap'
      | 'dataValidationRules'
      // | 'depNumberFormats'
      // | 'filters'
      // | 'usersPreferences'
      // | 'cells'
      // | 'results'
    >,
    // Pick<
    SheetView {
  // | 'viewport'
  // | 'selections'
  // | 'activeCell'
  // | 'activeCellArea'
  // | 'temporarySelection'
  // | 'colViewport'
  // | 'rowViewport'
  // | 'filterResults'
  // | 'isLocalFilter'
  // | 'localFilters'
  // >
  formulaBarHeight: number;
  groupBarHeight: number;
  colHeaderHeight: number;
  colFilterHeight: number;
  rowHeaderWidth: number;
  rowGroupBarWidth: number;
  freezedRowsSum: number;
  freezedColsSum: number;
  shortcutBarWidth: number;
  selecting: boolean;
}

interface SheetSet {
  sheet: Sheet | undefined;
  sheetView: SheetView | undefined;
  selecting: boolean;
}
export const selectSheetSections = (sheetId: string) =>
  createSelector(
    (state: RootState) => ({
      sheet: selectSheet(state, sheetId),
      sheetView: selectSheetView(state, sheetId),
      selecting: !!state.sheetsManager.selecting,
    }),
    ({
      sheet,
      sheetView,
      selecting,
    }: SheetSet): ReturnTypeofSheetSectionsSelector => {
      const colViewport = sheetView?.colViewport ?? getDefaultVirtualViewport();
      const rowViewport = sheetView?.rowViewport ?? getDefaultVirtualViewport();

      const zoom = sheet?.zoom ?? 1;
      const colHeaderHeight =
        getSectionHeight(sheet?.dynamicTopSection?.columnHeaderBar) * zoom; // colHeader?.isVisible ? colHeader?.height : 25;

      const colFilterHeight =
        getSectionHeight(sheet?.dynamicTopSection?.filterBar) * zoom; // colFilter?.isVisible ? colFilter?.height : 28;
      const groupBarHeight = rowViewport.alternateEntityGroupBarSize * zoom;
      // colViewport.groupBarSize
      //getSectionHeight(sheet?.dynamicTopSection.groupBar) * zoom;

      const formulaBarHeight = getSectionHeight(sheet?.topSection.formulaBar);

      const rowGroupBarWidth = colViewport.alternateEntityGroupBarSize * zoom;
      // getSectionWidth(sheet?.leftSection.rowGroupBar) * zoom;

      const shortcutBarWidth = getSectionWidth(sheet?.rightSection.shortcutBar);

      const freezedCols = sheet?.freezedCols ?? defaultFreeze();
      const freezedRows = sheet?.freezedRows ?? defaultFreeze();

      // const vcs = freezedCols.frozen ? freezedCols.end + 1 : 0;
      // const vrs = freezedRows.frozen ? freezedRows.end + 1 : 0;

      const colSizes = sheet?.colSizes ?? [];
      const rowSizes = sheet?.rowSizes ?? [];

      const defaultColWidth =
        sheet?.defaultColWidth ?? SheetDefaults.defaultColWidth;
      const defaultRowHeight =
        sheet?.defaultRowHeight ?? SheetDefaults.defaultRowHeight;

      // const hiddenRows = sheet?.hiddenRows ?? [];
      // const hiddenCols = sheet?.hiddenCols ?? [];

      // const filterResult = sheet?.filterResult ?? defaultFilterResult();

      // const viewport = sheetView?.viewport ?? {
      //   cs: vcs,
      //   ce: vcs,
      //   rs: vrs,
      //   re: vrs,
      // };

      // console.log('viewport', viewport);

      // const freezeViewport = createVirtualViewportForward(
      //   freezedRows.start,
      //   freezedRows.end,
      //   rows,
      //   defaultRowHeight,
      //   hiddenRows,
      //   zoom,
      //   freezedRows,
      //   filterResult
      // );

      // const rowViewport = createVirtualViewportForward(
      //   viewport.rs,
      //   viewport.re,
      //   rows,
      //   defaultRowHeight,
      //   hiddenRows,
      //   zoom,
      //   freezedRows,
      //   filterResult
      // );

      // console.log("freezeViewport",freezeViewport)
      // console.log('rowViewport', rowViewport);

      // const lastRe = _.last(rowViewport.nonFrozenIndexes) ?? 0;
      const { end } = getViewportStartEnd(rowViewport.nonFrozenIndexes);

      // console.log("rowViewport", rowViewport)
      // console.log("colViewport", colViewport)

      return {
        defaultColWidth,
        defaultRowHeight,
        colSizes,
        rowSizes,
        formulaBarHeight,
        groupBarHeight,
        colHeaderHeight,
        colFilterHeight,
        freezedCols,
        freezedRows,
        zoom,
        shortcutBarWidth,
        rowGroupBarWidth,

        colGroups: sheet?.colGroups ?? [],
        rowGroups: sheet?.rowGroups ?? [],

        cells: sheet?.cells ?? [],

        hiddenRows: sheet?.hiddenRows ?? [],
        hiddenCols: sheet?.hiddenCols ?? [],

        maxColIndex: sheet?.maxColIndex ?? 0,
        maxRowIndex: sheet?.maxRowIndex ?? 0,

        merges: sheet?.merges ?? [],

        // filterResult: filterResult,
        filterResults: sheetView?.filterResults ?? defaultFilterResults(),
        freezedRowsSum: rowViewport.frozenSum,
        freezedColsSum: colViewport.frozenSum, //getFreezedSum(cols, freezedCols, defaultColWidth, zoom),

        colViewport,
        rowViewport,

        rowHeaderWidth: getRowHeaderWidth(end, zoom), // TODO: Convert index to filter index and then calculate width
        // viewport: viewport,
        activeCell: sheetView?.activeCell ?? getDefaultPosition(),
        activeCellArea: sheetView?.activeCellArea ?? getDefaultArea(),
        selections: sheetView?.selections ?? [],
        selecting,
        temporarySelection: sheetView?.temporarySelection,

        // styles: sheet?.styles ?? {},
        // cellStyles: sheet?.cellStyles ?? {},
        // dataValidations: sheet?.dataValidations ?? {},
        dataValidationMatrix: sheet?.dataValidationMatrix ?? [],
        dataValidationsMap: sheet?.dataValidationsMap ?? {},
        dataValidationRules: sheet?.dataValidationRules ?? {},
        // conditionalFormats: sheet?.conditionalFormats ?? [],
        cellsReferences: sheet?.cellsReferences ?? [],
        numberFormatMatrix: sheet?.numberFormatMatrix ?? [],
        numberFormats: sheet?.numberFormats ?? {},
        stylesMatrix: sheet?.stylesMatrix ?? [],
        styles: sheet?.styles ?? {},
        // depNumberFormats: sheet?.depNumberFormats ?? {},
        // filters: sheet?.filters ?? {},
        // usersPreferences: sheet?.usersPreferences ?? {},

        // isLocalFilter: sheetView?.isLocalFilter,
        // localFilters: sheetView?.localFilters,
        // cells: sheet?.cells ?? {},
        // results: sheet?.results ?? {},
      };
    }
  );

// getFreezedSum(
//   rows,
//   freezedRows,
//   defaultRowHeight,
//   zoom,
//   filterResult
// )
